import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/components/core/_infrastructure/classes/services/global.service';
import { FilterSearchingBaseViewModel, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { FilterSearchingTwAforoViewModel, AforoItemResultsViewModel } from '../aforos.types';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { catchError, of, tap } from 'rxjs';
import { CampaniasService } from 'src/app/components/core/_infrastructure/classes/services/campanias-global.service';
import { ConfirmationService } from 'primeng/api';
import { CultivoService } from '../../../../_campanias/cultivos/cultivo.service';
import { CultivoViewModel } from '../../../../_campanias/cultivos/cultivo.types';
import { RecintoService } from '../../../../_campanias/recintos/recinto.service';
import { TwRecintoViewModel } from '../../../../_campanias/recintos/recinto.types';
import { equalObjects } from '../../../../_campanias/types/solicitudcam-datosbasicos.types';
import { AforoCultivoCamService } from '../../../../_campanias/unidades-inspeccion/aforo-cultivo.service';
import { VariedadService } from '../../../especies/variedades/variedad.service';
import { TwTcVariedadCamViewModel } from '../../../especies/variedades/variedad.types';

@Component({
    selector: 'app-aforo-wrapper',
    templateUrl: './aforo-wrapper.component.html',
})
export class AforoWrapperComponent extends ComponentBase implements OnInit {
    visibilitySpinnerExportar = false;
    searching = false;

    result: AforoItemResultsViewModel | null;
    payload?: FilterSearchingTwAforoViewModel;
    readonly = false;

    comunidadesAutonomas: Tc[];
    provincias: Tc[];
    recintos: TwRecintoViewModel[];
    cultivos: CultivoViewModel[];
    variedades: TwTcVariedadCamViewModel[];

    private route = '/mantenimiento/aforo';

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        protected campaniasService: CampaniasService,
        private globalService: GlobalService,
        private aforoService: AforoCultivoCamService,
        private recintoService: RecintoService,
        private variedadService: VariedadService,
        private cultivoService: CultivoService,
        private confirmationService: ConfirmationService,
        private router: Router,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.recoverLastSearch();
        this.getListas();
    }

    /**
     * We try to get back the previous search done by the user.
     */
    private recoverLastSearch() {
        this.payload = this.aforoService.getLastSearch();
    }

    private getListas(): void {
        this.loadComunidadesAutonomas();
        this.loadVariedades();
        if (!this.payload) {
            return;
        }
        this.loadDependentLists();
    }

    private loadDependentLists(): void {
        if (this.payload?.IdComunidadAutonoma) {
            this.onComunidadAutonomaSelected(this.payload?.IdComunidadAutonoma);
        }
        if (this.payload?.IdProvincia) {
            this.onProvinciaSelected(this.payload?.IdProvincia);
        }
        if (this.payload?.IdRecinto) {
            this.onRecintoSelected(this.payload?.IdRecinto);
        }
    }

    private loadComunidadesAutonomas() {
        this.globalService.getComunidadesAutonomas().subscribe(res => {
            this.comunidadesAutonomas = res;
        });
    }

    onComunidadAutonomaSelected(comunidadAutonoma: number): void {
        this.globalService.getProvinciasByComunidad(comunidadAutonoma).subscribe(res => {
            this.provincias = res;
        });
    }

    onProvinciaSelected(provincia: number): void {
        if (provincia === null) {
            return;
        }
        this.recintoService.getRecintosByIdProvincia(provincia).subscribe(res => {
            this.recintos = res;
        });
    }

    onRecintoSelected(recinto: number): void {
        if (recinto === null) {
            return;
        }
        this.cultivoService.getAllCultivosByIdRecinto(recinto).subscribe(res => {
            this.cultivos = res;
        });
    }

    private loadVariedades() {
        this.variedadService.getAllVariedades().subscribe(res => {
            this.variedades = res;
        });
    }

    /**
     * Respond to the action of searching on aforo-filter.
     * It will send the filter to the backend and set the response.
     * @param payload FilterSearchingTwAforoViewModel
     * @return void
     */
    onSearch(payload: FilterSearchingTwAforoViewModel): void {
        if (!equalObjects(this.payload, payload)) {
            // As soon as the user changes the "filter", the previous payload pagination values have no sense
            // When recovering the request params from the last result, the pagination params won't be reset.
            payload = this.clearPaginationFilter(payload);
        }

        this.payload = { ...payload } as FilterSearchingTwAforoViewModel; // To keep a copy to be used in the lazy search (paginator)
        if (!payload) {
            return;
        }

        this.searching = true;
        this.result = null;

        // En el listado normal de aforos, consultamos la tabla TwAforoCultivoCam
        this.aforoService
            .getAforos(this.payload)
            .pipe(
                tap(results => {
                    this.result = results;
                    this.searching = true;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter(payload: FilterSearchingTwAforoViewModel): FilterSearchingTwAforoViewModel {
        if (!payload) {
            return payload;
        }
        payload.PageNumber = 0;
        payload.Asc = 0;
        payload.OrderBy = '';

        return payload;
    }

    /**
     * It will be invoked when using the paginator
     * @param lazyLoadParams FilterSearchingBaseViewModel
     * @return void
     */
    onSearchLazy(lazyLoadParams: FilterSearchingBaseViewModel): void {
        this.payload = {
            ...this.payload,
            PageNumber: lazyLoadParams.PageNumber,
            Asc: lazyLoadParams.Asc,
            OrderBy: lazyLoadParams.OrderBy,
        } as FilterSearchingTwAforoViewModel;
        this.onSearch(this.payload);
    }

    /**
     * Invoked when the user wants to clear the form
     * @return void
     */
    onReset(): void {
        this.searching = false;
        this.result = null;
        this.aforoService.resetFilter();
        this.provincias = [];
        this.recintos = [];
        this.cultivos = [];
    }

    /**
     * Redirigimos al formulario de creación de un nuevo Aforo
     * @return void
     */
    onAdd(): void {
        this.router.navigate([this.route, 'create']);
    }

    /**
     * Redirigimos al formulario de edición del Aforo.
     * Cuando el parámetro sea null, el backend captura el error y se muestra en pantalla.
     * @param itemId number|null
     * @return void
     */
    onEdit(itemId: number | null): void {
        this.router.navigate([this.route, 'edit', itemId]);
    }

    onDelete(itemId: number): void {
        this.confirmationService.confirm({
            message: 'Va a eliminar el Aforo seleccionado, ¿está seguro?',
            accept: () => {
                this.aforoService
                    .deleteAforo(itemId)
                    .pipe(
                        tap(() => {
                            this.addSuccessMessage('Aforo eliminado correctamente');
                            this.recoverLastSearch();
                            this.onSearch(this.payload!);
                        }),
                        catchError(error => {
                            this.catchServerError(error);
                            return of(null);
                        }),
                    )
                    .subscribe();
            },
        });
    }

    /**
     * Invoked when the user wants to export the result to excel
     * @return void
     */
    onExportExcel(): void {
        this.visibilitySpinnerExportar = true;

        let model: FilterSearchingTwAforoViewModel = new FilterSearchingTwAforoViewModel();
        Object.assign(model, this.payload);

        this.aforoService.export(model).subscribe(res => {
            this.OpenDownloadedFile(res);
            this.visibilitySpinnerExportar = false;
        });
    }

    /**
     * To check whether there are results to export
     * @returns boolean
     */
    exportIsAvailable(): boolean {
        return this.result?.ResultList ? this.result?.ResultList?.length > 0 : false;
    }
}
